import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Building2,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquare,
  Settings,
  TrendingUp,
  User,
  UserPlus,
  Users,
} from 'lucide-react';
import { AppShell, BenachrichtigungenProvider, useBenachrichtigungen, type NavItem } from '@elbklar/ui';
import { useAuth } from '@/features/auth/AuthContext';
import { benachrichtigungenApi } from '@/lib/api';
import { kontoUrl } from '@/lib/konto';
import { RouterLink } from './RouterLink';
import { SchnellErfassenSheets, type SchnellWahl } from './SchnellErfassenSheets';

/**
 * Rahmen jeder angemeldeten CRM-Seite. Benachrichtigungen kommen als Modul aus
 * `@elbklar/ui` (Zähler, Panel mit Neu, Alle und Einstellungen); ihr Eintrag
 * steht im Sidebar-Fuß, am Handy unter „Mehr“.
 */
export function CRMLayout() {
  const navigate = useNavigate();
  return (
    <BenachrichtigungenProvider app="crm" api={benachrichtigungenApi} navigieren={navigate} kontoEinstellungen={() => kontoUrl('/benachrichtigungen')}>
      <Rahmen />
    </BenachrichtigungenProvider>
  );
}

function Rahmen() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Erstes Pfadsegment = Bereich: /customers/123/edit -> customers
  const aktiv = pathname.split('/')[1] || 'dashboard';
  const benachrichtigungen = useBenachrichtigungen();
  const [schnellOffen, setSchnellOffen] = useState(false);
  const [schnellWahl, setSchnellWahl] = useState<SchnellWahl | null>(null);

  // Persoenliche Einstellungen stehen hier bewusst NICHT. Sie haengen am User,
  // nicht an dieser App, und liegen seit dem 01.09. auf konto.elbklar.com —
  // erreichbar ueber das Benutzermenue oben rechts (am Handy ueber „Mehr“).
  //
  // "Einstellungen" bleibt: das ist Tenant-Config (Branding, Rechtliches,
  // Vorlagen) und damit Firmensache. Sie gehoert der Geschaeftsfuehrung, also
  // dem Berechtigungsprofil `leitung` (ADR-0009); `role === 'admin'` gilt
  // uebergangsweise, bis `users.role` faellt.
  const darfEinstellungen = user?.profile === 'leitung' || user?.role === 'admin';
  // Benachrichtigungen zuerst, in jeder App derselbe Ort (Henri, 26.10.2026).
  const fuss: NavItem[] = [
    benachrichtigungen.eintrag,
    ...(darfEinstellungen ? [{ id: 'settings', label: 'Einstellungen', icon: Settings, href: '/settings' }] : []),
  ];

  // Am Handy gibt es das Benutzermenü im Seitenkopf nicht; Profil und
  // Abmelden stehen in „Mehr“ unter den übrigen Zielen und dem Fuß der Sidebar.
  const mehrItems: NavItem[] = [
    { id: 'profil', label: 'Profil', icon: User },
    { id: 'abmelden', label: 'Abmelden', icon: LogOut },
  ];

  const waehlen = async (id: string) => {
    if (benachrichtigungen.waehlen(id)) return;
    if (id === 'profil') window.location.href = kontoUrl('/profil');
    else if (id === 'abmelden') {
      await logout();
      navigate('/login');
    }
  };

  return (
    <>
      <AppShell
        sidebar={{
          app: 'CRM',
          // Die Suche sitzt oben im Seitenkopf (GlobalActions), am Handy oben
          // in „Kunden“: Sie braucht eine Trefferliste, die das Sidebar-Feld
          // nicht hat.
          search: false,
          activeId: aktiv,
          onSelect: waehlen,
          linkAs: RouterLink,
          sections: [
            { items: [{ id: 'dashboard', label: 'Übersicht', icon: LayoutDashboard, href: '/dashboard' }] },
            {
              label: 'Vertrieb',
              items: [
                { id: 'leads', label: 'Leads', icon: UserPlus, href: '/leads' },
                { id: 'customers', label: 'Kunden', icon: Building2, href: '/customers' },
                { id: 'partners', label: 'Partner', icon: Briefcase, href: '/partners' },
                { id: 'pipeline', label: 'Pipeline', icon: TrendingUp, href: '/pipeline' },
              ],
            },
            {
              label: 'Arbeit',
              items: [
                { id: 'contacts', label: 'Kontakte', icon: Users, href: '/contacts' },
                { id: 'interactions', label: 'Interaktionen', icon: MessageSquare, href: '/interactions' },
                { id: 'emails', label: 'E-Mails', icon: Mail, href: '/emails' },
                { id: 'tasks', label: 'Aufgaben', icon: ClipboardList, href: '/tasks' },
              ],
            },
          ],
          footer: fuss,
        }}
        // Tab-Leiste unten am Handy (M1): Übersicht · Kunden · + · Aufgaben · Mehr.
        // Das „+“ öffnet Schnell erfassen (M3).
        mobilNav={{
          tabs: ['dashboard', 'customers', 'tasks'],
          schnellErfassen: { label: 'Schnell erfassen', onClick: () => setSchnellOffen(true) },
          mehrItems,
        }}
      >
        <Outlet />
      </AppShell>
      <SchnellErfassenSheets open={schnellOffen} onOpenChange={setSchnellOffen} wahl={schnellWahl} onWahl={setSchnellWahl} />
    </>
  );
}
